// src/app/components/dialogs/JudgesModal.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import Table, { buttonsStyle } from "../tables/table";
import ConfirmDialog from "./ConfirmDialog";
import { alertError, alertSuccess } from "../../../utils/logger";

import { getUsers } from "../../../api/user";
import { getGpsDevices } from "../../../api/gpsdevice";
import {
  getRegattaJudgesByRegatta,
  postRegattaJudge,
  putRegattaJudge,
  deleteRegattaJudge,
} from "../../../api/regattajudge";

import JudgeDialog from "./JudgeDialog";

export default function JudgesModal({ show, onHide, regattaId, onJudgesChange }) {
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]); // [{ _id, user, gpsDevice, gpsId }]
  const [allJudges, setAllJudges] = useState([]);     // [{ _id, fullName, email }]
  const [gpsDevices, setGpsDevices] = useState([]);   // [{ _id, code, phone }]

  const [showJudgeDialog, setShowJudgeDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  useEffect(() => {
    if (show && regattaId) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, regattaId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // 1) Cargar asignaciones de jueces para la regata
      const listRes = await getRegattaJudgesByRegatta(regattaId);
      if (listRes.status === 200) {
        const list = (listRes.data?.judges || []).map(a => ({
          _id: a._id,
          user: typeof a.user === "object" ? a.user?._id : a.user,
          gpsDevice: typeof a.gpsDevice === "object" ? a.gpsDevice?._id : a.gpsDevice || null,
          gpsId: a.gpsId || "",
          latitude: typeof a.latitude === "number" ? a.latitude : (a.latitude ?? null),
          longitude: typeof a.longitude === "number" ? a.longitude : (a.longitude ?? null),
        }));
        setAssignments(list);
        if (onJudgesChange) onJudgesChange(regattaId, list.length);
      }

      // 2) Catálogo de jueces (Users filtrados por role=judge)
      const usersRes = await getUsers();
      if (usersRes.status === 200) {
        setAllJudges((usersRes.data || []).filter(u => u.role === "judge"));
      }

      // 3) Dispositivos GPS
      const gpsRes = await getGpsDevices();
      if (gpsRes.status === 200) setGpsDevices(gpsRes.data || []);
    } catch (err) {
      alertError({ error: err, customMessage: "Could not load judges data" });
    } finally {
      setLoading(false);
    }
  };

  const judgeById = (id) => allJudges.find(j => j._id === id);
  const gpsById = (id) => gpsDevices.find(g => g._id === id);
  
  const formatCoord = (v) => {
    const n = Number(String(v).replace(",", "."));
    if (!Number.isFinite(n)) return "";
    return n.toFixed(6).replace(/\.?0+$/, "");
  };

  const hasCoords = (row) =>
    row &&
    row.latitude !== null && row.latitude !== undefined && row.latitude !== "" &&
    row.longitude !== null && row.longitude !== undefined && row.longitude !== "";

  const columns = [
    {
      dataField: "user",
      text: "Judge",
      formatter: (userId) => {
        const j = judgeById(userId);
        return j ? `${j.fullName} (${j.email})` : "–";
      },
      sort: true,
    },
    {
      text: "GPS / Coords",
      dataField: "gpsDevice", // usamos row en el formatter
      formatter: (gpsDeviceId, row) => {
        // 1) Si hay coordenadas, priorízalas y enlaza a Google Maps
        if (hasCoords(row)) {
          const lat = formatCoord(row.latitude);
          const lon = formatCoord(row.longitude);
          if (lat && lon) {
            const href = `https://maps.google.com/?q=${lat},${lon}`;
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {lat}, {lon}
              </a>
            );
          }
        }

        // 2) Si no hay coords, muestra el dispositivo
        if (gpsDeviceId) {
          const g = gpsById(gpsDeviceId);
          if (g) return g.phone ? `${g.code} (${g.phone})` : g.code;
        }

        // 3) Si no hay device, pero sí código manual
        if (row?.gpsId) return row.gpsId;

        // 4) Nada disponible
        return "–";
      },
      sort: true,
      align: "center",
      headerAlign: "center",
    },
    {
      text: "Actions",
      formatter: (cell, row, rowIndex) => (
        <>
          <IconButton
            size="small"
            style={buttonsStyle}
            onClick={() => {
              setSelectedAssignment({ ...row, index: rowIndex });
              setShowJudgeDialog(true);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            style={buttonsStyle}
            onClick={() => setAssignmentToDelete({ ...row, index: rowIndex })}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
      align: "center",
    },
  ];

  const upsertAssignment = async (assignment, index = null) => {
    try {
      // Evita duplicados del mismo juez en la regata
      const judgeId = assignment.user;
      const dup = assignments.findIndex((a, i) => a.user === judgeId && i !== index);
      if (dup !== -1) {
        alertError({ error: null, customMessage: "This judge is already assigned to the regatta." });
        return;
      }

      if (index === null || index === undefined || !assignments[index]?._id) {
        // CREATE
        const res = await postRegattaJudge({
          regatta: regattaId,
          user: assignment.user,
          gpsDevice: assignment.gpsDevice || null,
          gpsId: assignment.gpsId || undefined,
          latitude: assignment.latitude ?? undefined,
          longitude: assignment.longitude ?? undefined,
        });
        if (res.status === 201) {
          const a = res.data;
          const created = {
            _id: a._id,
            user: typeof a.user === "object" ? a.user?._id : a.user,
            gpsDevice: typeof a.gpsDevice === "object" ? a.gpsDevice?._id : a.gpsDevice || null,
            gpsId: a.gpsId || "",
            latitude: a.latitude ?? null,
            longitude: a.longitude ?? null,
          };
          const next = [...assignments, created];
          setAssignments(next);
          if (onJudgesChange) onJudgesChange(regattaId, next.length);
          alertSuccess({ title: "Saved", customMessage: "Judge added successfully" });
        }
      } else {
        // UPDATE (solo GPS / gpsId)
        const id = assignments[index]._id;
        const res = await putRegattaJudge(id, {
          gpsDevice: assignment.gpsDevice || null,
          gpsId: assignment.gpsId || undefined,
          latitude: assignment.latitude ?? undefined,
          longitude: assignment.longitude ?? undefined,
        });
        if (res.status === 200) {
          const next = [...assignments];
          next[index] = { ...next[index], ...assignment };
          setAssignments(next);
          alertSuccess({ title: "Saved", customMessage: "Judge updated successfully" });
        }
      }
    } catch (err) {
      alertError({ error: err, customMessage: "Could not save judge assignment" });
    }
  };

  const removeAssignment = async (index) => {
    try {
      const id = assignments[index]?._id;
      if (!id) {
        // nada que borrar en server (inconsistencia local)
        const next = assignments.filter((_, i) => i !== index);
        setAssignments(next);
        if (onJudgesChange) onJudgesChange(regattaId, next.length);
        return;
      }
      const res = await deleteRegattaJudge(id);
      if (res.status === 204) {
        const next = assignments.filter((_, i) => i !== index);
        setAssignments(next);
        if (onJudgesChange) onJudgesChange(regattaId, next.length);
        alertSuccess({ title: "Deleted", customMessage: "Judge removed successfully" });
      }
    } catch (err) {
      alertError({ error: err, customMessage: "Could not delete judge assignment" });
    } finally {
      setAssignmentToDelete(null);
    }
  };

  return (
    <>
      <Dialog open={show} onClose={onHide} fullWidth maxWidth="md">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Judges ({assignments.length})</Typography>
            <Button
              variant="contained"
              className="btn btn-primary"
              onClick={() => {
                setSelectedAssignment(null);
                setShowJudgeDialog(true);
              }}
              disabled={loading}
            >
              Add judge
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table keyField="_id" data={assignments} columns={columns} />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onHide} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {showJudgeDialog && (
        <JudgeDialog
          open={showJudgeDialog}
          onClose={() => {
            setShowJudgeDialog(false);
            setSelectedAssignment(null);
          }}
          onSave={(payload) => {
            const idx =
              selectedAssignment && typeof selectedAssignment.index === "number"
                ? selectedAssignment.index
                : null;
            upsertAssignment(payload, idx);
            setShowJudgeDialog(false);
            setSelectedAssignment(null);
          }}
          assignment={selectedAssignment}
          judges={allJudges}
          gpsDevices={gpsDevices}
        />
      )}

      <ConfirmDialog
        open={!!assignmentToDelete}
        setOpen={(v) => {
          if (!v) setAssignmentToDelete(null);
        }}
        title="Are you sure you want to remove this judge from the regatta?"
        onConfirm={() => {
          if (assignmentToDelete && typeof assignmentToDelete.index === "number") {
            removeAssignment(assignmentToDelete.index);
          }
        }}
      />
    </>
  );
}
